<template>
  <div>
    <div
      class="flex flex-col"
      :class="position"
    >
      <div
        :ref="`miniMultiSelector-${uid}`"
        :class="`flex w-full border rounded border-gray-300 bg-white px-1 shadow-sm ${selectedOptions.length ? '!py-1.5' : '!max-h-[47px]'} ${customClass} ${disabled ? 'bg-zinc-300 cursor-not-allowed pointer-events-none' : 'cursor-pointer bg-white'}`"
      >
        <div class="flex flex-wrap flex-auto">
          <button
            class="px-3 py-0.5 text-sm font-medium whitespace-nowrap"
            :class="disabled ? 'text-zinc-400' : ''"
            :disabled="disabled"
            @click="toggleSelector"
          >
            {{ inputPlaceholder }}
          </button>
        </div>
        <button
          type="button"
          class="flex items-center gap-2 pl-2 pr-1 text-gray-600 cursor-pointer focus:outline-none"
          :disabled="disabled"
          @click.stop="toggleSelector"
        >
          <span class="text-gray-300">|</span>
          <ChevronDownSmall
            class="text-gray-500 duration-150 transform"
            :class="{ 'rotate-180': comboOpen }"
          />
        </button>
      </div>
      <div
        v-show="comboOpen"
        :ref="`miniMultiSelector-options-${uid}`"
        :class="listBoxClass"
        class="fixed z-50 py-1 mt-2 overflow-auto bg-white border-2 border-gray-200 shadow-lg h-min max-h-96 min-w-max"
        :style="{ top: dropdownTop + 'px', left: dropdownLeft + 'px', width: dropdownWidth + 'px' }"
      >
        <div class="relative py-1 pl-3 pr-2 overflow-hidden border-b border-transparent border-b-gray-300">
          <div class="absolute left-4 top-3">
            <SearchIcon class="w-5 h-5 mx-auto text-slate-600" />
          </div>
          <input
            v-model="searchQuery"
            type="text"
            class="w-full p-2 pl-8 text-sm border-transparent border-none focus:shadow-outline ring-0 focus:border-transparent focus:ring-0"
            placeholder="Search..."
          >
          <button
            v-if="searchQuery"
            type="button"
            class="absolute right-4 top-1.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-green-light/80 text-main-primary hover:text-gray-700 focus:outline-none"
            @click.stop="clearInput"
          >
            <TimesSmall custom-style="w-4 h-4" />
          </button>
        </div>
        <ul
          class="text-base ring-1 ring-black/5 focus:outline-none sm:text-sm"
          role="listbox"
        >
          <li
            v-for="{ active, id, name, selected } in filteredOptions"
            :key="`${uid}-${id}`"
            class="relative py-2 pl-3 text-gray-900 cursor-default select-none pr-9"
            :class="{ 'bg-zuno-turquoise text-slate-50': active, 'text-gray-900': !active }"
            role="option"
            tabindex="-1"
            @click.stop="toggleSelection(id)"
            @mouseenter="toggleActivation(id, 'on')"
            @mouseleave="toggleActivation(id)"
          >
            <span
              class="block truncate"
              :class="{ 'font-semibold': selected }"
            > {{ name }}</span>
            <span
              v-if="selected"
              class="absolute inset-y-0 right-0 flex items-center pr-4"
              :class="{ 'text-slate-50': active, 'text-zuno-turquoise': !active }"
            >
              <CheckSmall />
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'MiniMultiSelector',

  props: {
    optionsToEdit: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      required: true,
    },
    displayProp: {
      type: String,
      required: true,
    },
    valueProp: {
      type: String,
      required: true,
    },
    placeholderText: {
      type: String,
      default: 'Please select...',
    },
    listBoxClass: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      default: 'items-center',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    resource: {
      type: String,
      default: '',
    },
  },

  emits: ['valuesUpdated', 'input'],

  data() {
    return {
      uid: null,
      dropdownTop: 0,
      dropdownLeft: 0,
      dropdownWidth: 0,
      definedOptions: this.attachDefaultOptionsProps([...this.options]),
      comboOpen: false,
      selectedOptions: [],
      searchQuery: '',
      isFetchingNextPage: false,
      threshold: 50,
    }
  },
  computed: {
    disabled() {
      return this.isDisabled
    },
    inputPlaceholder() {
      return this.selectedOptions.length === 0 ? this.placeholderText : `${this.selectedOptions.length} ${this.resource} selected`
    },
    filteredOptions() {
      return !this.searchQuery ? this.definedOptions : [...this.definedOptions].filter(({ name }) => name.toLowerCase().includes(this.searchQuery.toLowerCase()))
    },
  },

  watch: {
    comboOpen(val) {
      nextTick(() => {
        if (val) {
          document.body.addEventListener('click', this.handleOutsideClick)
          document.body.addEventListener('keydown', this.handleEscapeKey)
          this.positionDropdown()
          this.positionDropdown = debounce(this.positionDropdown, 50)
          document.addEventListener('scroll', this.positionDropdown, true)
        }
        else {
          document.body.removeEventListener('click', this.handleOutsideClick)
        }
      })
    },
    options(newOptions) {
      this.definedOptions = this.attachDefaultOptionsProps(newOptions).map((option) => {
        option.selected = [...this.optionsToEdit].map(newOption => newOption[this.valueProp]).includes(option[this.valueProp])
        return option
      })
    },
    optionsToEdit: {
      handler(newOptions) {
        this.selectedOptions = this.attachDefaultOptionsProps(newOptions)
        this.definedOptions = this.definedOptions.map((option) => {
          option.selected = [...newOptions].map(newOption => newOption[this.valueProp]).includes(option[this.valueProp])
          return option
        })
      },
      immediate: true,
    },
  },

  mounted() {
    this.uid = this.$.uid
  },
  methods: {
    positionDropdown() {
      const multiSelector = this.$refs[`miniMultiSelector-${this.uid}`]
      const multiSelectorOptions = this.$refs[`miniMultiSelector-options-${this.uid}`]

      const { top, right, left } = multiSelector.getBoundingClientRect()
      const { height: multiSelectorOptionsHeight, width: multiSelectorOptionsWidth } = multiSelectorOptions.getBoundingClientRect()
      const { height: multiSelectorHeight, width: multiSelectWidth } = multiSelector.getBoundingClientRect()
      const { innerHeight, innerWidth } = window

      if (top + multiSelectorHeight + multiSelectorOptionsHeight > innerHeight) {
        this.dropdownTop = top - multiSelectorOptionsHeight
      }
      else {
        this.dropdownTop = top + multiSelectorHeight
      }

      if (left + multiSelectorOptionsWidth > innerWidth) {
        this.dropdownLeft = innerWidth - multiSelectorOptionsWidth - 10
      }
      else {
        this.dropdownLeft = right - multiSelectWidth
      }

      this.dropdownWidth = multiSelectWidth
    },
    handleOutsideClick(event) {
      // If the clicked target is not within the dropdown list or the dropdown input,
      // set comboOpen to false to close the dropdown and remove the click event listener from the document.
      if (!this.$refs[`miniMultiSelector-options-${this.uid}`].contains(event.target) && !this.$refs[`miniMultiSelector-${this.uid}`].contains(event.target)) {
        this.closeDropdownAndkillAllListeners()
      }
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape') {
        this.closeDropdownAndkillAllListeners()
      }
    },
    killAllListeners() {
      document.body.removeEventListener('click', this.handleOutsideClick)
      document.body.removeEventListener('keydown', this.handleEscapeKey)
      document.removeEventListener('scroll', this.positionDropdown, true)
      this.searchQuery = ''
    },
    closeDropdownAndkillAllListeners() {
      this.closeCombo()
      this.killAllListeners()
    },
    closeCombo() {
      this.comboOpen = false
    },
    attachDefaultOptionsProps(options) {
      const optionsToAttach = JSON.parse(JSON.stringify(options))

      return optionsToAttach.map((option) => {
        option.active = false
        option.selected = false
        option.name = option[this.displayProp]
        option.value = option[this.valueProp]
        return option
      })
    },
    toggleActivation(id, status = 'off') {
      this.definedOptions = this.definedOptions.map((option) => {
        if (id === option.id) {
          option.active = status === 'on'
        }
        return option
      })
    },
    clearInput() {
      this.searchQuery = ''
    },
    toggleSelection(id) {
      this.definedOptions = [...this.definedOptions].map((option) => {
        if (id === option.id) {
          option.selected = !option.selected
        }
        return option
      })
      this.selectedOptions = [...this.definedOptions].filter(({ selected }) => selected)
      this.$emit(
        'valuesUpdated',
        [...this.selectedOptions].map(option => option.value),
      )
      this.$emit(
        'input',
        [...this.selectedOptions].map(option => option.value),
      )
    },
    deselectOption(value) {
      const optionId = this.definedOptions.find(option => option.value === value).id
      this.toggleSelection(optionId)
    },
    toggleSelector() {
      this.comboOpen = !this.comboOpen
      nextTick(() => {
        if (!this.isOpen) {
          this.killAllListeners()
        }
      })
    },
  },
}
</script>
